import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatErr, todayLocal } from "@/lib/api";

const DAY_STYLE = {
  P: "bg-status-green/15 text-status-green",
  A: "bg-status-red/10 text-status-red",
  L: "bg-status-blue/10 text-status-blue",
};

const LANGUAGE_LOCALE = {
  en: "en-IN",
  hi: "hi-IN",
  kn: "kn-IN",
  te: "te-IN",
  mr: "mr-IN",
};

export default function ParentAttendanceCalendar({ child }) {
  const { t, i18n } = useTranslation();

  const maxMonth = todayLocal().slice(0, 7);
  const minMonth = `${parseInt(maxMonth.slice(0, 4), 10) - 3}${maxMonth.slice(4)}`;

  const [month, setMonth] = useState(maxMonth);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(null);

  const locale = LANGUAGE_LOCALE[i18n.language] || "en-IN";

  const load = useCallback(async () => {
    try {
      const res = await api.get("/parent/attendance/monthly", {
        params: {
          month,
          student_id: child.id,
        },
      });
      setData(res.data);
    } catch (e) {
      toast.error(formatErr(e));
    }
  }, [month, child.id]);

  useEffect(() => {
    setSelected(null);
    load();
  }, [load]);

  const shift = (delta) => {
    const [y, m] = month.split("-").map(Number);
    const d = new Date(y, m - 1 + delta, 1);

    const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}`;

    if (next >= minMonth && next <= maxMonth) {
      setMonth(next);
    }
  };

  const cells = useMemo(() => {
    if (!data) return [];

    const first = new Date(`${data.dates[0]}T00:00:00`);
    const offset = (first.getDay() + 6) % 7;

    return [...Array(offset).fill(null), ...data.dates];
  }, [data]);

  const monthLabel = data
    ? new Date(`${data.dates[0]}T00:00:00`).toLocaleDateString(locale, {
        month: "long",
        year: "numeric",
      })
    : "";

  const formatSelectedDate = (date) =>
    new Date(`${date}T00:00:00`).toLocaleDateString(locale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    });

  const statusLabel = (status) => {
    switch (status) {
      case "P":
        return t("parentAttendance.status.present", {
          defaultValue: "Present",
        });

      case "A":
        return t("parentAttendance.status.absent", {
          defaultValue: "Absent",
        });

      case "L":
        return t("parentAttendance.status.approvedLeave", {
          defaultValue: "Approved Leave",
        });

      default:
        return t("parentAttendance.status.notMarked", {
          defaultValue: "Not Marked",
        });
    }
  };

  const weekdays = [
    t("parentAttendance.weekdays.mon", { defaultValue: "Mon" }),
    t("parentAttendance.weekdays.tue", { defaultValue: "Tue" }),
    t("parentAttendance.weekdays.wed", { defaultValue: "Wed" }),
    t("parentAttendance.weekdays.thu", { defaultValue: "Thu" }),
    t("parentAttendance.weekdays.fri", { defaultValue: "Fri" }),
    t("parentAttendance.weekdays.sat", { defaultValue: "Sat" }),
    t("parentAttendance.weekdays.sun", { defaultValue: "Sun" }),
  ];

  const s = data?.summary;

  return (
    <section
      className="mb-6"
      data-testid="parent-attendance-calendar"
    >
      <h2 className="font-heading font-bold text-sm text-slate-400 uppercase tracking-wider mb-2.5">
        {t("parentAttendance.heading", {
          defaultValue: "Attendance calendar",
        })}
      </h2>

      <div className="bg-white border border-slate-200 rounded-2xl p-4">
        <div className="flex items-center justify-between mb-3">
          <button
            data-testid="cal-prev-month"
            onClick={() => shift(-1)}
            disabled={month <= minMonth}
            className="p-2 rounded-full hover:bg-slate-100 disabled:opacity-30 transition-colors duration-200"
            aria-label={t("parentAttendance.previousMonth", {
              defaultValue: "Previous month",
            })}
          >
            <CaretLeft size={16} />
          </button>

          <p
            className="font-heading font-bold text-sm"
            data-testid="cal-month-label"
          >
            {monthLabel}
          </p>

          <button
            data-testid="cal-next-month"
            onClick={() => shift(1)}
            disabled={month >= maxMonth}
            className="p-2 rounded-full hover:bg-slate-100 disabled:opacity-30 transition-colors duration-200"
            aria-label={t("parentAttendance.nextMonth", {
              defaultValue: "Next month",
            })}
          >
            <CaretRight size={16} />
          </button>
        </div>

        {!data ? (
          <p className="text-sm text-slate-400 text-center py-6">
            {t("parentAttendance.loading", {
              defaultValue: "Loading…",
            })}
          </p>
        ) : !data.has_records ? (
          <p
            className="text-sm text-slate-400 text-center py-6"
            data-testid="cal-empty-state"
          >
            {t("parentAttendance.noRecords", {
              defaultValue:
                "No attendance records available for the selected period.",
            })}
          </p>
        ) : (
          <>
            <div
              className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 text-xs"
              data-testid="cal-summary"
            >
              <span className="font-bold">
                {t("parentAttendance.attendance", {
                  defaultValue: "Attendance",
                })}
                : {s.pct === null ? "—" : `${s.pct}%`}
              </span>

              <span className="text-status-green font-semibold">
                {t("parentAttendance.present", {
                  defaultValue: "Present",
                })}{" "}
                {s.present}
              </span>

              <span className="text-status-red font-semibold">
                {t("parentAttendance.absent", {
                  defaultValue: "Absent",
                })}{" "}
                {s.absent}
              </span>

              <span className="text-status-blue font-semibold">
                {t("parentAttendance.leave", {
                  defaultValue: "Leave",
                })}{" "}
                {s.leave}
              </span>
            </div>

            <div className="grid grid-cols-7 gap-1">
              {weekdays.map((d, index) => (
                <p
                  key={`${d}-${index}`}
                  className="text-center text-[10px] font-bold text-slate-400 uppercase"
                >
                  {d}
                </p>
              ))}

              {cells.map((d, i) =>
                d === null ? (
                  <div key={`blank-${i}`} />
                ) : (
                  <button
                    key={d}
                    data-testid={`cal-day-${d}`}
                    onClick={() => setSelected(d)}
                    className={`aspect-square rounded-lg flex flex-col items-center justify-center transition-colors duration-150 ${
                      DAY_STYLE[data.cells[d]] ||
                      "text-slate-300 hover:bg-slate-50"
                    } ${
                      selected === d
                        ? "ring-2 ring-[#00194E]"
                        : ""
                    }`}
                  >
                    <span className="text-xs font-semibold">
                      {parseInt(d.slice(8), 10)}
                    </span>

                    {data.cells[d] !== "—" && (
                      <span className="text-[9px] font-extrabold leading-none mt-0.5">
                        {data.cells[d]}
                      </span>
                    )}
                  </button>
                )
              )}
            </div>

            {selected && (
              <div
                className="mt-3 bg-[#F4F5F7] rounded-xl p-3.5"
                data-testid="attendance-day-detail"
              >
                <p className="font-heading font-bold text-sm">
                  {formatSelectedDate(selected)}
                </p>

                <p className="text-sm mt-1">
                  {t("parentAttendance.attendance", {
                    defaultValue: "Attendance",
                  })}
                  :{" "}
                  <span className="font-bold">
                    {statusLabel(data.cells[selected])}
                  </span>
                </p>

                <p className="text-xs text-slate-500 mt-0.5">
                  {t("parentAttendance.class", {
                    defaultValue: "Class",
                  })}
                  : {data.class_name}
                </p>

                {data.leave_reasons?.[selected] && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    {t("parentAttendance.leaveReason", {
                      defaultValue: "Leave reason",
                    })}
                    : {data.leave_reasons[selected]}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}